// frontend/src/components/Company/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Toast, { useToast } from '../Shared/Toast';

const PROVIDER_MODELS = {
  groq: [
    { value: 'llama-3.1-8b-instant', label: 'Llama 3.1 8B Instant (fast, free)' },
    { value: 'llama3-8b-8192', label: 'Llama 3 8B' },
    { value: 'mixtral-8x7b-32768', label: 'Mixtral 8x7B' },
    { value: 'gemma2-9b-it', label: 'Gemma 2 9B' }
  ],
  anthropic: [
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (fast, affordable)' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (balanced)' },
    { value: 'claude-opus-4-6', label: 'Claude Opus 4.6 (most capable)' }
  ],
  gemini: [
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash (fast, free)' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' }
  ]
};

const PROVIDER_INFO = {
  groq: { label: 'Groq', keyHint: 'Get free API key at console.groq.com', color: 'from-orange-400 to-red-500' },
  anthropic: { label: 'Anthropic (Claude)', keyHint: 'Get API key at console.anthropic.com', color: 'from-purple-500 to-indigo-600' },
  gemini: { label: 'Google Gemini', keyHint: 'Get free API key at aistudio.google.com', color: 'from-blue-400 to-cyan-500' }
};

const CompanyDashboard = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showSessionKey, setShowSessionKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });

  // Transcript wipe state
  const [wipeConfirm, setWipeConfirm] = useState(null); // null | 'all' | userId
  const [wipeLoading, setWipeLoading] = useState(false);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteTarget, setInviteTarget] = useState(null);        // user object
  const [selectedRecipients, setSelectedRecipients] = useState([]); // extra email strings
  const [manualEmailInput, setManualEmailInput] = useState('');
  const [inviteGenerating, setInviteGenerating] = useState(false);

  // Company features (granted by super admin)
  const [companyFeatures, setCompanyFeatures] = useState({ screenShare: false, remoteControl: false });
  const [featuresLoaded, setFeaturesLoaded] = useState(false);

  // AI Settings state
  const [showAISettings, setShowAISettings] = useState(false);
  const [aiSettings, setAiSettings] = useState({ aiProvider: 'groq', aiModel: '', aiApiKey: '', hasKey: false, maskedKey: '' });
  const [aiForm, setAiForm] = useState({ aiProvider: 'groq', aiModel: '', aiApiKey: '' });
  const [aiSaving, setAiSaving] = useState(false);

  // Session Duration state
  const [showSessionSettings, setShowSessionSettings] = useState(false);
  const [sessionHours, setSessionHours] = useState(2);
  const [sessionSaving, setSessionSaving] = useState(false);

  // Cloud Storage state
  const [showCloudSettings, setShowCloudSettings] = useState(false);
  const [cloudSettings, setCloudSettings] = useState({ provider: null, bucket: '', region: '', projectId: '', hasS3Keys: false, hasGcpKey: false, maskedAccessKey: '' });
  const [cloudForm, setCloudForm] = useState({ provider: 'none', bucket: '', region: '', accessKeyId: '', secretAccessKey: '', projectId: '', keyFileJson: '' });
  const [cloudSaving, setCloudSaving] = useState(false);

  const { toast, showToast } = useToast();

  useEffect(() => {
    fetchUsers();
    fetchSessions();
    fetchAISettings();
    fetchSessionSettings();
    fetchCloudSettings();
    fetchCompanyFeatures();
  }, []);

  const fetchCompanyFeatures = async () => {
    try {
      const res = await api.get('/settings/features');
      setCompanyFeatures(res.data);
    } catch (e) {
      console.error('fetch features:', e);
    } finally {
      setFeaturesLoaded(true);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await api.get('/sessions/company');
      setSessions(response.data.sessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', formData);
      setFormData({ firstName: '', lastName: '', email: '', password: '' });
      setShowCreateUser(false);
      fetchUsers();
      showToast('User created successfully!', 'success');
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to create user', 'error');
    }
  };

  // Open the invite modal for a given user row
  const openInviteModal = (targetUser) => {
    setInviteTarget(targetUser);
    setSelectedRecipients([]);
    setManualEmailInput('');
    setShowInviteModal(true);
  };

  // Toggle a user's email in/out of the extra recipients list
  const toggleRecipient = (email) => {
    setSelectedRecipients(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  // Add a manually-typed email
  const addManualEmail = () => {
    const trimmed = manualEmailInput.trim().toLowerCase();
    if (!trimmed || !/\S+@\S+\.\S+/.test(trimmed)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }
    if (!selectedRecipients.includes(trimmed) && trimmed !== inviteTarget?.email?.toLowerCase()) {
      setSelectedRecipients(prev => [...prev, trimmed]);
    }
    setManualEmailInput('');
  };

  // Generate key — with or without sending invite emails
  const handleGenerateSessionKey = async (sendInvites) => {
    if (!inviteTarget) return;
    setInviteGenerating(true);
    try {
      // Always include the target user's own email when sending invites
      // (backend deduplicates, but needs at least 1 entry to trigger the send path)
      const recipients = sendInvites
        ? [...new Set([inviteTarget.email.toLowerCase(), ...selectedRecipients])]
        : [];
      const response = await api.post('/sessions/create', {
        userId: inviteTarget.id,
        recipients,
      });
      setShowInviteModal(false);
      setShowSessionKey({ ...response.data.session, invitesSent: response.data.invitesSent || 0 });
      fetchSessions();
      if (sendInvites && response.data.invitesSent > 0) {
        showToast(`Session key sent to ${response.data.invitesSent} recipient(s)!`, 'success');
      }
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to generate session key', 'error');
    } finally {
      setInviteGenerating(false);
    }
  };

  const fetchAISettings = async () => {
    try {
      const response = await api.get('/settings/ai');
      setAiSettings(response.data);
      setAiForm({
        aiProvider: response.data.aiProvider || 'groq',
        aiModel: response.data.aiModel || '',
        aiApiKey: ''
      });
    } catch (error) {
      console.error('Error fetching AI settings:', error);
    }
  };

  const handleSaveAISettings = async (e) => {
    e.preventDefault();
    setAiSaving(true);
    try {
      await api.post('/settings/ai', aiForm);
      showToast('AI settings saved!', 'success');
      await fetchAISettings();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to save AI settings', 'error');
    } finally {
      setAiSaving(false);
    }
  };

  const fetchSessionSettings = async () => {
    try {
      const res = await api.get('/settings/session');
      setSessionHours(res.data.sessionDurationHours || 2);
    } catch (e) { console.error('fetch session settings:', e); }
  };

  const handleSaveSessionSettings = async (e) => {
    e.preventDefault();
    setSessionSaving(true);
    try {
      await api.post('/settings/session', { sessionDurationHours: sessionHours });
      showToast(`Session duration set to ${sessionHours} hours`, 'success');
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to save', 'error');
    } finally { setSessionSaving(false); }
  };

  const fetchCloudSettings = async () => {
    try {
      const res = await api.get('/settings/cloud');
      setCloudSettings(res.data);
      setCloudForm(f => ({
        ...f,
        provider: res.data.provider || 'none',
        bucket: res.data.bucket || '',
        region: res.data.region || '',
        projectId: res.data.projectId || ''
      }));
    } catch (e) { console.error('fetch cloud settings:', e); }
  };

  const handleSaveCloudSettings = async (e) => {
    e.preventDefault();
    setCloudSaving(true);
    try {
      await api.post('/settings/cloud', cloudForm);
      showToast('Cloud storage settings saved!', 'success');
      await fetchCloudSettings();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to save', 'error');
    } finally { setCloudSaving(false); }
  };

  const handleWipeTranscripts = async () => {
    setWipeLoading(true);
    try {
      let res;
      if (wipeConfirm === 'all') {
        res = await api.delete('/transcripts/company/all');
      } else {
        res = await api.delete(`/transcripts/company/user/${wipeConfirm}`);
      }
      showToast(res.data.message, 'success');
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to wipe transcripts', 'error');
    } finally {
      setWipeLoading(false);
      setWipeConfirm(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Session key copied to clipboard!', 'success');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toast toast={toast} />

      {/* Header */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-full p-2">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold text-gray-800">Company Dashboard</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Company Info Card */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl shadow-xl p-8 text-white mb-8">
          <h2 className="text-3xl font-bold mb-2">{user.name}</h2>
          <p className="text-blue-100">{user.email}</p>
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <p className="text-sm text-blue-100">Total Users</p>
              <p className="text-3xl font-bold">{users.length}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <p className="text-sm text-blue-100">Active Sessions</p>
              <p className="text-3xl font-bold">{sessions.filter(s => s.isActive && !s.isExpired).length}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <p className="text-sm text-blue-100">Total Sessions</p>
              <p className="text-3xl font-bold">{sessions.length}</p>
            </div>
          </div>
        </div>

        {/* ── Plan Features Card ─────────────────────────────────────────────── */}
        {featuresLoaded && (
          <div className="bg-white rounded-xl shadow-lg p-5 mb-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-1.5">
                  <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="text-sm font-bold text-gray-800">Room Features</span>
                <span className="text-xs text-gray-400">— enabled by your administrator</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* Screen Share */}
              <div className={`flex items-center space-x-3 p-3 rounded-xl border-2 ${
                companyFeatures.screenShare
                  ? 'border-blue-200 bg-blue-50'
                  : 'border-gray-100 bg-gray-50'
              }`}>
                <div className={`rounded-lg p-2 flex-shrink-0 ${
                  companyFeatures.screenShare ? 'bg-blue-500' : 'bg-gray-300'
                }`}>
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800">Screen Share</p>
                  <p className={`text-xs font-medium ${companyFeatures.screenShare ? 'text-blue-600' : 'text-gray-400'}`}>
                    {companyFeatures.screenShare ? '✓ Enabled' : '✗ Not enabled'}
                  </p>
                </div>
              </div>

              {/* Remote Control */}
              <div className={`flex items-center space-x-3 p-3 rounded-xl border-2 ${
                companyFeatures.remoteControl
                  ? 'border-orange-200 bg-orange-50'
                  : 'border-gray-100 bg-gray-50'
              }`}>
                <div className={`rounded-lg p-2 flex-shrink-0 ${
                  companyFeatures.remoteControl ? 'bg-orange-500' : 'bg-gray-300'
                }`}>
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800">Remote Control</p>
                  <p className={`text-xs font-medium ${companyFeatures.remoteControl ? 'text-orange-600' : 'text-gray-400'}`}>
                    {companyFeatures.remoteControl ? '✓ Enabled' : '✗ Not enabled'}
                  </p>
                </div>
              </div>
            </div>
            {!companyFeatures.screenShare && !companyFeatures.remoteControl && (
              <p className="mt-3 text-xs text-gray-400 text-center">
                Contact your administrator to enable screen sharing or remote control for your account.
              </p>
            )}
          </div>
        )}

        {/* Create User Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowCreateUser(!showCreateUser)}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg font-medium"
          >
            + Create New User
          </button>
        </div>

        {/* Create User Form */}
        {showCreateUser && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Create New User</h3>
            <form onSubmit={handleCreateUser} className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <div className="col-span-2 flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  Create User
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateUser(false)}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* AI Settings */}
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <button
            onClick={() => setShowAISettings(!showAISettings)}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors border-b border-gray-200"
          >
            <div className="flex items-center space-x-3">
              <div className={`bg-gradient-to-r ${PROVIDER_INFO[aiSettings.aiProvider]?.color || 'from-gray-400 to-gray-600'} rounded-lg p-2`}>
                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-gray-800">AI Provider Settings</p>
                <p className="text-xs text-gray-500">
                  Current: <span className="font-medium text-gray-700">{PROVIDER_INFO[aiSettings.aiProvider]?.label || aiSettings.aiProvider}</span>
                  {aiSettings.hasKey
                    ? <span className="ml-2 text-green-600 font-medium">Key set ({aiSettings.maskedKey})</span>
                    : <span className="ml-2 text-red-500 font-medium">No API key set</span>}
                </p>
              </div>
            </div>
            <svg className={`h-5 w-5 text-gray-500 transition-transform ${showAISettings ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showAISettings && (
            <form onSubmit={handleSaveAISettings} className="p-6 space-y-5">
              {/* Provider selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">AI Provider</label>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(PROVIDER_INFO).map(([key, info]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setAiForm(f => ({ ...f, aiProvider: key, aiModel: '' }))}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        aiForm.aiProvider === key
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded bg-gradient-to-r ${info.color} mx-auto mb-1`} />
                      {info.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  {PROVIDER_INFO[aiForm.aiProvider]?.keyHint}
                </p>
              </div>

              {/* Model selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Model</label>
                <select
                  value={aiForm.aiModel}
                  onChange={(e) => setAiForm(f => ({ ...f, aiModel: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                >
                  <option value="">Default model for provider</option>
                  {(PROVIDER_MODELS[aiForm.aiProvider] || []).map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  API Key
                  {aiSettings.hasKey && aiSettings.aiProvider === aiForm.aiProvider && (
                    <span className="ml-2 text-xs font-normal text-gray-400">(leave blank to keep existing: {aiSettings.maskedKey})</span>
                  )}
                </label>
                <input
                  type="password"
                  placeholder={aiSettings.hasKey && aiSettings.aiProvider === aiForm.aiProvider ? 'Leave blank to keep current key' : 'Paste your API key here'}
                  value={aiForm.aiApiKey}
                  onChange={(e) => setAiForm(f => ({ ...f, aiApiKey: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm font-mono"
                />
              </div>

              <button
                type="submit"
                disabled={aiSaving}
                className="w-full py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium disabled:opacity-60"
              >
                {aiSaving ? 'Saving...' : 'Save AI Settings'}
              </button>
            </form>
          )}
        </div>

        {/* Session Duration Settings */}
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <button
            onClick={() => setShowSessionSettings(!showSessionSettings)}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors border-b border-gray-200"
          >
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-teal-400 to-cyan-500 rounded-lg p-2">
                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-gray-800">Session Duration</p>
                <p className="text-xs text-gray-500">
                  Default: <span className="font-medium text-gray-700">{sessionHours} hour{sessionHours !== 1 ? 's' : ''}</span>
                  <span className="ml-2 text-gray-400">— applies to new session keys and stealth agent timer</span>
                </p>
              </div>
            </div>
            <svg className={`h-5 w-5 text-gray-500 transition-transform ${showSessionSettings ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showSessionSettings && (
            <form onSubmit={handleSaveSessionSettings} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Session Duration (hours)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="number"
                    min="0.5"
                    max="168"
                    step="0.5"
                    value={sessionHours}
                    onChange={e => setSessionHours(parseFloat(e.target.value))}
                    className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <span className="text-sm text-gray-500">hours (0.5 – 168)</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[1, 2, 4, 8, 24, 48].map(h => (
                    <button key={h} type="button" onClick={() => setSessionHours(h)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        sessionHours === h
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}>
                      {h}h
                    </button>
                  ))}
                </div>
              </div>
              <button type="submit" disabled={sessionSaving}
                className="w-full py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg hover:from-teal-600 hover:to-cyan-700 transition-all font-medium disabled:opacity-60">
                {sessionSaving ? 'Saving...' : 'Save Session Duration'}
              </button>
            </form>
          )}
        </div>

        {/* Cloud Storage Settings */}
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <button
            onClick={() => setShowCloudSettings(!showCloudSettings)}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors border-b border-gray-200"
          >
            <div className="flex items-center space-x-3">
              <div className={`rounded-lg p-2 ${cloudSettings.provider ? 'bg-gradient-to-r from-blue-500 to-indigo-600' : 'bg-gradient-to-r from-gray-400 to-gray-500'}`}>
                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-gray-800">Cloud Transcript Storage</p>
                <p className="text-xs text-gray-500">
                  {cloudSettings.provider
                    ? <span className="text-green-600 font-medium">Active — {cloudSettings.provider.toUpperCase()} / {cloudSettings.bucket}</span>
                    : <span className="text-orange-500 font-medium">Not configured — transcripts stay in DB only</span>
                  }
                </p>
              </div>
            </div>
            <svg className={`h-5 w-5 text-gray-500 transition-transform ${showCloudSettings ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showCloudSettings && (
            <form onSubmit={handleSaveCloudSettings} className="p-6 space-y-5">
              {/* Provider */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Storage Provider</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 's3', label: 'AWS S3', color: 'from-orange-400 to-yellow-500' },
                    { value: 'gcp', label: 'Google Cloud', color: 'from-blue-400 to-green-500' },
                    { value: 'none', label: 'Disabled', color: 'from-gray-300 to-gray-400' }
                  ].map(p => (
                    <button key={p.value} type="button"
                      onClick={() => setCloudForm(f => ({ ...f, provider: p.value }))}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        cloudForm.provider === p.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}>
                      <div className={`w-6 h-6 rounded bg-gradient-to-r ${p.color} mx-auto mb-1`} />
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {cloudForm.provider !== 'none' && (
                <>
                  {/* Bucket */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Bucket Name</label>
                    <input type="text" value={cloudForm.bucket}
                      onChange={e => setCloudForm(f => ({ ...f, bucket: e.target.value }))}
                      placeholder="my-transcripts-bucket"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                  </div>

                  {cloudForm.provider === 's3' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">AWS Region</label>
                        <input type="text" value={cloudForm.region}
                          onChange={e => setCloudForm(f => ({ ...f, region: e.target.value }))}
                          placeholder="us-east-1"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Access Key ID
                          {cloudSettings.hasS3Keys && <span className="ml-2 text-xs font-normal text-gray-400">(current: {cloudSettings.maskedAccessKey})</span>}
                        </label>
                        <input type="password" value={cloudForm.accessKeyId}
                          onChange={e => setCloudForm(f => ({ ...f, accessKeyId: e.target.value }))}
                          placeholder={cloudSettings.hasS3Keys ? 'Leave blank to keep existing' : 'AKIAIOSFODNN7EXAMPLE'}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-mono" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Secret Access Key</label>
                        <input type="password" value={cloudForm.secretAccessKey}
                          onChange={e => setCloudForm(f => ({ ...f, secretAccessKey: e.target.value }))}
                          placeholder={cloudSettings.hasS3Keys ? 'Leave blank to keep existing' : 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY'}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-mono" />
                      </div>
                    </>
                  )}

                  {cloudForm.provider === 'gcp' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">GCP Project ID</label>
                        <input type="text" value={cloudForm.projectId}
                          onChange={e => setCloudForm(f => ({ ...f, projectId: e.target.value }))}
                          placeholder="my-project-123456"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Service Account Key (JSON)
                          {cloudSettings.hasGcpKey && <span className="ml-2 text-xs font-normal text-green-600">Key set ✓</span>}
                        </label>
                        <textarea rows={4} value={cloudForm.keyFileJson}
                          onChange={e => setCloudForm(f => ({ ...f, keyFileJson: e.target.value }))}
                          placeholder={cloudSettings.hasGcpKey ? 'Leave blank to keep existing key' : 'Paste the contents of your service-account.json here'}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-mono resize-none" />
                        <p className="text-xs text-gray-400 mt-1">IAM → Service Accounts → Keys → Create JSON key</p>
                      </div>
                    </>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-700 font-medium mb-1">File Structure in Bucket:</p>
                    <p className="text-xs font-mono text-blue-600">
                      {cloudForm.bucket || 'your-bucket'}/YYYYMMDD/&lt;userId&gt;/user_YYYYMMDD.txt
                    </p>
                    <p className="text-xs text-blue-500 mt-1">One file per user per day — sessions append to same file</p>
                  </div>
                </>
              )}

              <button type="submit" disabled={cloudSaving}
                className="w-full py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all font-medium disabled:opacity-60">
                {cloudSaving ? 'Saving...' : 'Save Cloud Settings'}
              </button>
            </form>
          )}
        </div>

        {/* Transcript Management */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-red-400 to-orange-500 rounded-lg p-2">
                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">Transcript Management</h3>
                <p className="text-xs text-gray-500">Wipe stored transcripts to free up database space</p>
              </div>
            </div>
            <button
              onClick={() => setWipeConfirm('all')}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Wipe All Transcripts
            </button>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-800">Users</h3>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No users yet. Create your first user above.</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {u.firstName} {u.lastName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{u.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(u.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openInviteModal(u)}
                            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all"
                          >
                            Generate Session Key
                          </button>
                          <button
                            onClick={() => setWipeConfirm(u.id)}
                            title="Wipe transcripts for this user"
                            className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-600 text-sm rounded-lg transition-colors"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Wipe Confirmation Modal */}
      {wipeConfirm !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-red-100 rounded-full p-3">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Confirm Wipe</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              {wipeConfirm === 'all'
                ? 'Are you sure you want to permanently delete ALL transcripts for every user in your company?'
                : `Are you sure you want to permanently delete all transcripts for ${users.find(u => u.id === wipeConfirm)?.firstName} ${users.find(u => u.id === wipeConfirm)?.lastName}?`
              }
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleWipeTranscripts}
                disabled={wipeLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-60 transition-colors font-medium"
              >
                {wipeLoading ? 'Wiping...' : 'Yes, Wipe Transcripts'}
              </button>
              <button
                onClick={() => setWipeConfirm(null)}
                disabled={wipeLoading}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && inviteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center space-x-3 mb-1">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-2">
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Generate Session Key</h3>
                  <p className="text-sm text-gray-500">
                    For: <span className="font-semibold text-gray-700">{inviteTarget.firstName} {inviteTarget.lastName}</span>
                    <span className="ml-1 text-gray-400">({inviteTarget.email})</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">
              {/* Auto-included recipient notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
                <svg className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xs text-blue-700">
                  The session key will always be sent to <strong>{inviteTarget.email}</strong> (the user themselves).
                  Add more recipients below.
                </p>
              </div>

              {/* Other registered users — checkboxes */}
              {users.filter(u => u.id !== inviteTarget.id).length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Add registered users:</p>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
                    {users
                      .filter(u => u.id !== inviteTarget.id)
                      .map(u => {
                        const email = u.email.toLowerCase();
                        const checked = selectedRecipients.includes(email);
                        return (
                          <label
                            key={u.id}
                            className={`flex items-center space-x-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${checked ? 'bg-purple-50' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRecipient(email)}
                              className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-800 truncate">{u.firstName} {u.lastName}</p>
                              <p className="text-xs text-gray-400 truncate">{u.email}</p>
                            </div>
                          </label>
                        );
                      })
                    }
                  </div>
                </div>
              )}

              {/* Manual email input */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Add external email:</p>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    placeholder="someone@example.com"
                    value={manualEmailInput}
                    onChange={e => setManualEmailInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addManualEmail())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={addManualEmail}
                    className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 text-sm font-medium rounded-lg transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Selected recipients chips */}
              {selectedRecipients.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Additional recipients ({selectedRecipients.length}):</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedRecipients.map(email => (
                      <span
                        key={email}
                        className="inline-flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                      >
                        <span>{email}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedRecipients(prev => prev.filter(e => e !== email))}
                          className="ml-1 hover:text-purple-900 transition-colors"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer buttons */}
            <div className="px-6 pb-6 pt-4 border-t border-gray-100 flex-shrink-0 space-y-2">
              <button
                onClick={() => handleGenerateSessionKey(true)}
                disabled={inviteGenerating}
                className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium disabled:opacity-60 flex items-center justify-center space-x-2"
              >
                {inviteGenerating ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Generate &amp; Send Invites ({selectedRecipients.length + 1} recipient{selectedRecipients.length !== 0 ? 's' : ''})</span>
                  </>
                )}
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleGenerateSessionKey(false)}
                  disabled={inviteGenerating}
                  className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                >
                  Generate Only (no email)
                </button>
                <button
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviteGenerating}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm transition-colors disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Session Key Modal */}
      {showSessionKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-green-100 rounded-full p-2">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Session Key Generated</h3>
                <p className="text-sm text-gray-500">Expires in {showSessionKey.validFor}</p>
              </div>
            </div>

            {showSessionKey.invitesSent > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4 flex items-center space-x-2">
                <svg className="h-4 w-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-sm text-green-700">
                  Invite sent to <strong>{showSessionKey.invitesSent}</strong> recipient{showSessionKey.invitesSent !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <p className="text-xs text-gray-500 mb-1">Session Key:</p>
              <p className="font-mono text-sm break-all text-gray-800">{showSessionKey.sessionKey}</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => copyToClipboard(showSessionKey.sessionKey)}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                Copy Key
              </button>
              <button
                onClick={() => setShowSessionKey(null)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDashboard;
